import {
    CalendarOutlined,
    DeleteOutlined,
    EditOutlined,
    PlusCircleOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { Alert, Card, DatePicker, Form, Input, message, Modal, Switch, Table, Tag } from 'antd';
import ReusableButton from 'components/Elements/ReusableButton';
import EllipsisDropdown from 'components/shared-components/EllipsisDropdown';
import helperFunc from 'helpers/helperFunc';
import network from 'network/network';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const DATE_FORMAT = 'DD.MM.YYYY';
const API_PATH = '/admin/OfficialHoliday';

const OfficialHolidays = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [holidayList, setHolidayList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedHoliday, setSelectedHoliday] = useState(null);

    const getErrorMessage = useCallback((result, fallbackKey) => (
        result?.data?.message
        || result?.data?.errorMessage
        || result?.errorMessage
        || t(fallbackKey)
    ), [t]);

    const selectHolidayList = useCallback(async () => {
        setLoading(true);

        try {
            const result = await network.get(`${API_PATH}/selectHolidayList`, false);

            if (!result.isSuccess || result.data?.isSuccess === false) {
                message.error(getErrorMessage(result, 'holidayLoadError'));
                return;
            }

            const data = result.data?.data ?? result.data;
            setHolidayList(Array.isArray(data) ? data : []);
        } catch (error) {
            message.error(t('holidayLoadError'));
        } finally {
            setLoading(false);
        }
    }, [getErrorMessage, t]);

    useEffect(() => {
        selectHolidayList();
    }, [selectHolidayList]);

    const openCreateModal = () => {
        setSelectedHoliday(null);
        form.resetFields();
        form.setFieldsValue({ status: true });
        setModalOpen(true);
    };

    const openEditModal = (holiday) => {
        setSelectedHoliday(holiday);
        form.setFieldsValue({
            holidayName: holiday.holidayName,
            holidayDate: holiday.holidayDate ? dayjs(holiday.holidayDate) : null,
            status: holiday.status !== false,
        });
        setModalOpen(true);
    };

    const closeModal = () => {
        if (submitting) {
            return;
        }

        setModalOpen(false);
        setSelectedHoliday(null);
        form.resetFields();
    };

    const saveHoliday = async () => {
        let values;

        try {
            values = await form.validateFields();
        } catch (error) {
            return;
        }

        setSubmitting(true);

        const isUpdate = Boolean(selectedHoliday?.holidayId);
        const params = {
            holidayId: selectedHoliday?.holidayId || 0,
            holidayName: values.holidayName.trim(),
            holidayDate: values.holidayDate.format('YYYY-MM-DD'),
            status: values.status,
        };
        const method = isUpdate ? 'updateHoliday' : 'insertHoliday';

        try {
            const result = await network.post(`${API_PATH}/${method}`, params);

            if (!result.isSuccess || result.data?.isSuccess === false) {
                message.error(getErrorMessage(result, 'holidaySaveError'));
                return;
            }

            message.success(t(isUpdate ? 'holidayUpdatedSuccess' : 'holidaySavedSuccess'));
            setModalOpen(false);
            setSelectedHoliday(null);
            form.resetFields();
            await selectHolidayList();
        } catch (error) {
            message.error(t('holidaySaveError'));
        } finally {
            setSubmitting(false);
        }
    };

    const deleteHoliday = async (holiday) => {
        setLoading(true);

        try {
            const result = await network.post(
                `${API_PATH}/deleteHoliday/${holiday.holidayId}`,
                {},
            );

            if (!result.isSuccess || result.data?.isSuccess === false) {
                message.error(getErrorMessage(result, 'holidayDeleteError'));
                return;
            }

            message.success(t('holidayDeletedSuccess'));
            await selectHolidayList();
        } catch (error) {
            message.error(t('holidayDeleteError'));
        } finally {
            setLoading(false);
        }
    };

    const confirmDelete = (holiday) => {
        helperFunc.showConfirm({
            title: t('deleteHolidayConfirmTitle'),
            content: t('deleteHolidayConfirmDescription', { name: holiday.holidayName }),
            onConfirm: () => deleteHoliday(holiday),
        });
    };

    const filteredHolidays = useMemo(() => {
        const searchValue = searchTerm.trim().toLocaleLowerCase();

        if (!searchValue) {
            return holidayList;
        }

        return holidayList.filter((holiday) => {
            const name = holiday.holidayName?.toLocaleLowerCase() || '';
            const rawDate = String(holiday.holidayDate || '');
            const formattedDate = rawDate && dayjs(rawDate).isValid()
                ? dayjs(rawDate).format(DATE_FORMAT)
                : '';

            return name.includes(searchValue)
                || rawDate.toLocaleLowerCase().includes(searchValue)
                || formattedDate.includes(searchValue);
        });
    }, [holidayList, searchTerm]);

    const dropdownMenu = (holiday) => [
        {
            key: 'edit',
            label: t('edit'),
            icon: <EditOutlined />,
            onClick: () => openEditModal(holiday),
        },
        {
            key: 'delete',
            label: t('delete'),
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => confirmDelete(holiday),
        },
    ];

    const columns = [
        {
            title: t('holidayName'),
            dataIndex: 'holidayName',
            sorter: (first, second) => (first.holidayName || '').localeCompare(second.holidayName || ''),
        },
        {
            title: t('holidayDate'),
            dataIndex: 'holidayDate',
            sorter: (first, second) => dayjs(first.holidayDate).valueOf() - dayjs(second.holidayDate).valueOf(),
            render: (value) => value && dayjs(value).isValid() ? dayjs(value).format(DATE_FORMAT) : '-',
        },
        {
            title: t('status'),
            dataIndex: 'status',
            align: 'center',
            render: (value) => (
                <Tag color={value !== false ? 'green' : 'red'}>
                    {value !== false ? t('active') : t('inactive')}
                </Tag>
            ),
        },
        {
            title: t('actions'),
            key: 'actions',
            width: 100,
            align: 'center',
            render: (_, holiday) => (
                <EllipsisDropdown menu={{ items: dropdownMenu(holiday) }} />
            ),
        },
    ];

    return (
        <>
            <Helmet title={t('officialHolidays')} />
            <Card title={t('officialHolidays')}>
                <Alert
                    className="mb-3"
                    type="info"
                    showIcon
                    message={t('officialHolidayInfo')}
                />

                <div
                    className="mb-3"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 12,
                    }}
                >
                    <Input
                        allowClear
                        value={searchTerm}
                        onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder={t('searchOfficialHolidays')}
                        prefix={<SearchOutlined />}
                        style={{ width: 280, maxWidth: '100%' }}
                    />
                    <div style={{ width: 220, maxWidth: '100%' }}>
                        <ReusableButton
                            variant="campaign"
                            text={t('newOfficialHoliday')}
                            icon={<PlusCircleOutlined />}
                            onClick={openCreateModal}
                        />
                    </div>
                </div>

                <Table
                    columns={columns}
                    dataSource={filteredHolidays}
                    loading={loading}
                    rowKey="holidayId"
                    pagination={{
                        total: filteredHolidays.length,
                        showTotal: (total) => `${t('total')}: ${total}`,
                    }}
                    locale={{ emptyText: t('noOfficialHolidays') }}
                />
            </Card>

            <Modal
                title={t(selectedHoliday ? 'editOfficialHoliday' : 'addOfficialHoliday')}
                open={modalOpen}
                okText={t('save')}
                cancelText={t('cancel')}
                confirmLoading={submitting}
                onOk={saveHoliday}
                onCancel={closeModal}
                maskClosable={!submitting}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ status: true }}
                    preserve={false}
                >
                    <Form.Item
                        name="holidayName"
                        label={t('holidayName')}
                        rules={[
                            { required: true, whitespace: true, message: t('holidayNameRequired') },
                            { max: 200, message: t('holidayNameMaxLength') },
                        ]}
                    >
                        <Input
                            maxLength={200}
                            placeholder={t('holidayNamePlaceholder')}
                            prefix={<CalendarOutlined />}
                        />
                    </Form.Item>

                    <Form.Item
                        name="holidayDate"
                        label={t('holidayDate')}
                        rules={[{ required: true, message: t('holidayDateRequired') }]}
                    >
                        <DatePicker
                            format={DATE_FORMAT}
                            placeholder={t('selectHolidayDate')}
                            style={{ width: '100%' }}
                        />
                    </Form.Item>

                    <Form.Item
                        name="status"
                        label={t('status')}
                        valuePropName="checked"
                    >
                        <Switch checkedChildren={t('active')} unCheckedChildren={t('inactive')} />
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default OfficialHolidays;
